import styled from "styled-components"
import colors from "~constants/colors"

interface AISuggestionBoxProps {
  invalidationReason: string
  onAcceptSuggestion: () => void
  isProcessing: boolean
  // Disabled once the operator edits the form away from the suggested values.
  disabled?: boolean
}

const formatReason = (reason: string) =>
  reason
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())

const AISuggestionBox = ({
  invalidationReason,
  onAcceptSuggestion,
  isProcessing,
  disabled = false,
}: AISuggestionBoxProps) => {
  return (
    <Container>
      <Inner>
        <IconCircle />
        <Title>{formatReason(invalidationReason)}</Title>
        {disabled && !isProcessing && (
          <EditedHint>Edited — revert to accept</EditedHint>
        )}
        <ActionButton
          onClick={onAcceptSuggestion}
          disabled={isProcessing || disabled}
          title={
            disabled
              ? "Revert your edits to the suggested values to accept"
              : undefined
          }
        >
          {isProcessing ? "Processing..." : "Accept & Reject"}
        </ActionButton>
      </Inner>
    </Container>
  )
}

// The box (background) spans the full row; Inner keeps the content left-aligned
// and caps its width so the action button lands near the screen's center.
const Container = styled.div`
  padding: 8px 14px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
`

const Inner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 720px;
`

const IconCircle = styled.div`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${colors.red};
  position: relative;

  &::before,
  &::after {
    content: "";
    position: absolute;
    background: white;
    border-radius: 1px;
  }
  &::before {
    width: 2px;
    height: 8px;
    top: 5px;
    left: 10px;
  }
  &::after {
    width: 2px;
    height: 2px;
    bottom: 5px;
    left: 10px;
  }
`

const Title = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #7f1d1d;
`

const ActionButton = styled.button`
  flex-shrink: 0;
  background: ${colors.red};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 0.15s,
    transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const EditedHint = styled.div`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  color: #991b1b;
`

export default AISuggestionBox
